import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import AppHeader from '../components/AppHeader';
import { User, Phone, MapPin, Camera, Save, Loader2, ArrowLeft, ArrowRight, Mail, Building2, Shield, Globe, ChevronDown, MessageCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import countries from '../data/countries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { t, language } = useLanguage();
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();

  const [phone, setPhone] = useState(user?.company?.phone || user?.phone || '');
  const [address, setAddress] = useState(user?.company?.address || '');
  const [country, setCountry] = useState(user?.company?.country || '');
  const [picture, setPicture] = useState(user?.picture || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Sync local state when user data changes (e.g. from mobile update)
  useEffect(() => {
    if (user) {
      setPhone(user.company?.phone || user.phone || '');
      setAddress(user.company?.address || '');
      setCountry(user.company?.country || '');
      setPicture(user.picture || '');
    }
  }, [user?.updated_at, user?.company?.updated_at]);

  // OTP states for phone change
  const originalPhone = user?.company?.phone || user?.phone || '';
  const phoneChanged = phone !== originalPhone && phone.trim().length >= 8;
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);

  useEffect(() => {
    if (otpCountdown > 0) {
      const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCountdown]);

  const handlePhoneChange = (val) => {
    setPhone(val);
    setPhoneVerified(false);
    setOtpSent(false);
    setOtpCode('');
  };

  const sendPhoneOTP = async () => {
    if (!phone || phone.length < 8) {
      toast.error(language === 'ar' ? 'يرجى إدخال رقم هاتف صحيح' : 'Please enter a valid phone number');
      return;
    }
    setOtpLoading(true);
    try {
      await axios.post(`${API}/otp/send`, { phone });
      setOtpSent(true);
      setOtpCountdown(60);
      toast.success(language === 'ar' ? 'تم إرسال رمز التحقق عبر واتساب' : 'OTP sent via WhatsApp');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      toast.error(detail || (language === 'ar' ? 'فشل إرسال رمز التحقق' : 'Failed to send OTP'));
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyPhoneOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error(language === 'ar' ? 'يرجى إدخال رمز مكون من 6 أرقام' : 'Please enter a 6-digit code');
      return;
    }
    setOtpLoading(true);
    try {
      await axios.post(`${API}/otp/verify`, { phone, code: otpCode });
      setPhoneVerified(true);
      toast.success(language === 'ar' ? 'تم التحقق من رقم الهاتف بنجاح' : 'Phone verified successfully');
    } catch (err) {
      toast.error(language === 'ar' ? 'رمز التحقق غير صحيح أو منتهي الصلاحية' : 'Invalid or expired OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPicture(data.url);
      toast.success(language === 'ar' ? 'تم رفع الصورة' : 'Image uploaded');
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل رفع الصورة' : 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    // Check if phone changed and requires OTP verification
    if (phoneChanged && !phoneVerified) {
      toast.error(language === 'ar' ? 'يرجى التحقق من رقم الهاتف الجديد أولاً' : 'Please verify your new phone number first');
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/auth/profile`, { phone, address, country, picture });
      await checkAuth();
      toast.success(language === 'ar' ? 'تم تحديث البروفايل بنجاح' : 'Profile updated successfully');
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      toast.error(detail || (language === 'ar' ? 'فشل تحديث البروفايل' : 'Failed to update profile'));
    } finally {
      setSaving(false);
    }
  };

  const BackIcon = language === 'ar' ? ArrowRight : ArrowLeft;

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <AppHeader />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          data-testid="profile-back-btn"
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <BackIcon size={18} />
          {language === 'ar' ? 'رجوع' : 'Back'}
        </button>

        <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-primary/5 to-accent/5 p-6 sm:p-8 text-center">
            {/* Profile Picture */}
            <div className="relative inline-block mb-4" data-testid="profile-picture-section">
              <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-white shadow-lg mx-auto bg-gray-100">
                {picture ? (
                  <img
                    src={picture.startsWith('http') ? picture : `${process.env.REACT_APP_BACKEND_URL}${picture}`}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    data-testid="profile-picture-img"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-accent/10" data-testid="profile-picture-placeholder">
                    <User size={40} className="text-accent/40" />
                  </div>
                )}
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="profile-upload-btn"
                className="absolute bottom-0 right-0 w-10 h-10 bg-accent text-white rounded-full flex items-center justify-center shadow-md hover:bg-accent/90 transition-all border-2 border-white"
              >
                {uploading ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                data-testid="profile-file-input"
              />
            </div>

            <h1 className="text-2xl font-heading font-bold text-primary" data-testid="profile-name">{user?.name}</h1>
            <p className="text-sm text-muted-foreground flex items-center justify-center gap-1.5 mt-1">
              <Mail size={14} />{user?.email}
            </p>
          </div>

          {/* Read-only Info */}
          <div className="px-6 sm:px-8 py-5 border-b border-border bg-gray-50/50">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3" data-testid="profile-company-info">
                <div className="w-9 h-9 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Building2 size={18} className="text-accent" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{language === 'ar' ? 'الشركة' : 'Company'}</p>
                  <p className="text-sm font-semibold text-foreground">{user?.company?.name || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3" data-testid="profile-status-info">
                <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
                  <Shield size={18} className="text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{language === 'ar' ? 'الحالة' : 'Status'}</p>
                  <p className="text-sm font-semibold text-foreground">{t(user?.company?.status || 'pending')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Editable Fields */}
          <div className="px-6 sm:px-8 py-6 space-y-5">
            <h2 className="text-base font-heading font-bold text-primary flex items-center gap-2">
              {language === 'ar' ? 'تعديل المعلومات' : 'Edit Information'}
            </h2>

            {/* Phone */}
            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-1.5">
                {language === 'ar' ? 'رقم الهاتف' : 'Phone Number'}
              </label>
              <div className="relative">
                <Phone size={18} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder={language === 'ar' ? 'أدخل رقم الهاتف' : 'Enter phone number'}
                  data-testid="profile-phone-input"
                  className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10' : 'pl-10'} border ${phoneChanged && phoneVerified ? 'border-green-400 bg-green-50' : 'border-border'} rounded-lg focus:outline-none focus:ring-2 focus:ring-accent`}
                />
                {phoneChanged && phoneVerified && (
                  <CheckCircle size={18} className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-green-500`} />
                )}
              </div>

              {/* OTP for phone change */}
              {phoneChanged && !phoneVerified && (
                <div className="mt-2 space-y-2">
                  {!otpSent ? (
                    <button
                      type="button"
                      onClick={sendPhoneOTP}
                      disabled={otpLoading}
                      data-testid="profile-send-otp-btn"
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-all"
                    >
                      <MessageCircle size={18} />
                      {otpLoading
                        ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...')
                        : (language === 'ar' ? 'إرسال رمز التحقق عبر واتساب' : 'Send OTP via WhatsApp')}
                    </button>
                  ) : (
                    <>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          data-testid="profile-otp-code-input"
                          className="flex-1 px-4 py-2.5 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-center text-lg tracking-widest font-mono"
                          placeholder="000000"
                          maxLength={6}
                        />
                        <button
                          type="button"
                          onClick={verifyPhoneOTP}
                          disabled={otpLoading || otpCode.length !== 6}
                          data-testid="profile-verify-otp-btn"
                          className="px-4 py-2.5 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent/90 disabled:opacity-50 transition-all"
                        >
                          {otpLoading
                            ? (language === 'ar' ? 'تحقق...' : 'Verifying...')
                            : (language === 'ar' ? 'تحقق' : 'Verify')}
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={sendPhoneOTP}
                        disabled={otpCountdown > 0 || otpLoading}
                        data-testid="profile-resend-otp-btn"
                        className="w-full text-sm text-muted-foreground hover:text-accent disabled:opacity-50 transition-colors"
                      >
                        {otpCountdown > 0
                          ? (language === 'ar' ? `إعادة الإرسال بعد ${otpCountdown} ثانية` : `Resend in ${otpCountdown}s`)
                          : (language === 'ar' ? 'إعادة إرسال الرمز' : 'Resend code')}
                      </button>
                    </>
                  )}
                </div>
              )}
              {phoneChanged && phoneVerified && (
                <p className="mt-1 text-xs text-green-600 font-medium" data-testid="profile-phone-verified-badge">
                  {language === 'ar' ? 'تم التحقق من الرقم الجديد' : 'New number verified'}
                </p>
              )}
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-1.5">
                {language === 'ar' ? 'العنوان' : 'Address'}
              </label>
              <div className="relative">
                <MapPin size={18} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-3 text-muted-foreground`} />
                <textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder={language === 'ar' ? 'أدخل العنوان' : 'Enter address'}
                  data-testid="profile-address-input"
                  rows={3}
                  className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10' : 'pl-10'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent resize-none`}
                />
              </div>
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-1.5">
                {language === 'ar' ? 'الدولة' : 'Country'}
              </label>
              <div className="relative">
                <Globe size={18} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  data-testid="profile-country-select"
                  className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10' : 'pl-10'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent bg-white appearance-none`}
                >
                  <option value="">{language === 'ar' ? 'اختر الدولة' : 'Select country'}</option>
                  {countries.map(c => (
                    <option key={c.code} value={c.code}>{c.flag} {language === 'ar' ? c.name_ar : c.name_en}</option>
                  ))}
                </select>
                <ChevronDown size={14} className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none`} />
              </div>
            </div>

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="profile-save-btn"
              className="w-full flex items-center justify-center gap-2 py-3 bg-accent text-white rounded-lg hover:bg-accent/90 font-semibold transition-all disabled:opacity-50 shadow-sm hover:shadow-md"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              {language === 'ar' ? 'حفظ التعديلات' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
